/**
 * Lead-management pieces for a training enquiry row: timestamped notes log,
 * call-back date + % chance fields, and the "Book onto course" picker.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarClock, Percent, Send, GraduationCap, Loader2, Bot, MessageSquare, UserPlus, X } from 'lucide-react';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { formatApiError } from '@/utils/apiError';
import { accentForCourse } from '@/pages/trainingBrand';

const BRAND_NAMES = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
const brandLabelForCourse = (c) => {
  const secs = c?.sections || [];
  return secs.length === 1 ? (BRAND_NAMES[secs[0]] || 'Course') : secs.length > 1 ? 'Multi' : 'Course';
};

const fmtWhen = (iso) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
const todayIso = () => new Date().toISOString().slice(0, 10);

export const callbackTone = (date, status) => {
  if (!date || ['booked', 'lost', 'archived'].includes(status)) return null;
  const t = todayIso();
  if (date < t) return 'overdue';
  if (date === t) return 'today';
  return 'upcoming';
};

/** Call-back date + % chance — saved on change. */
export function LeadFields({ row, onUpdate }) {
  const [chance, setChance] = useState(row.chance_percent ?? '');
  useEffect(() => { setChance(row.chance_percent ?? ''); }, [row.chance_percent]);
  const tone = callbackTone(row.callback_date, row.status);
  const toneCls = { overdue: 'border-rose-400 bg-rose-50 text-rose-800', today: 'border-amber-400 bg-amber-50 text-amber-900', upcoming: 'border-gray-200' }[tone] || 'border-gray-200';
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`lead-fields-${row.id}`}>
      <label className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-semibold ${toneCls}`} title="Call back date">
        <CalendarClock className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Call back</span>
        <input
          type="date" value={row.callback_date || ''} onChange={(e) => onUpdate(row.id, { callback_date: e.target.value || '' })}
          className="bg-transparent text-xs font-semibold outline-none" data-testid={`lead-callback-${row.id}`}
        />
        <input
          type="time" value={row.callback_time || ''} step="300" disabled={!row.callback_date} title={row.callback_date ? 'Call back time' : 'Pick a date first'}
          onChange={(e) => onUpdate(row.id, { callback_time: e.target.value || '' })}
          className="bg-transparent text-xs font-semibold outline-none disabled:opacity-40 w-[5.6rem]" data-testid={`lead-callback-time-${row.id}`}
        />
        {tone === 'overdue' && <span className="text-[10px] uppercase font-bold">overdue</span>}
        {tone === 'today' && <span className="text-[10px] uppercase font-bold">today</span>}
      </label>
      <label className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2 py-1 text-xs font-semibold" title="% chance of booking">
        <Percent className="w-3.5 h-3.5 text-gray-500" />
        <input
          type="number" min="0" max="100" step="5" value={chance} placeholder="—"
          onChange={(e) => setChance(e.target.value)}
          onBlur={() => { const v = chance === '' ? null : Math.max(0, Math.min(100, Number(chance))); if (v !== (row.chance_percent ?? null) && v !== null) onUpdate(row.id, { chance_percent: v }); }}
          className="w-12 bg-transparent text-xs font-semibold outline-none text-right" data-testid={`lead-chance-${row.id}`}
        />
        <span className="text-gray-500">% chance</span>
      </label>
    </div>
  );
}

/** Append-only notes with author + timestamp; legacy single `notes` string shown as an imported entry. */
export function NotesLog({ row, onChanged }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const log = [...(row.notes_log || [])].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  if (row.notes && row.notes.trim() && !(row.notes_log || []).length) {
    log.push({ id: 'legacy', text: row.notes, author: 'Imported note', created_at: row.updated_at || row.created_at });
  }
  const visible = showAll ? log : log.slice(0, 3);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-enquiries/${row.id}/notes`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Could not add note');
      onChanged(body);
      setText('');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="border-t border-gray-100 px-3 sm:px-4 py-2.5 bg-gray-50" data-testid={`notes-log-${row.id}`}>
      <div className="flex gap-2">
        <Input
          value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); add(); } }}
          placeholder="Add a note — e.g. 'Pitched bound, call back Fri for deposit' (Enter to save)"
          className="bg-white text-sm h-9" data-testid={`note-input-${row.id}`}
        />
        <Button size="sm" onClick={add} disabled={busy || !text.trim()} className="h-9 bg-[#1f4030] hover:bg-[#16302a] text-white shrink-0" data-testid={`note-add-${row.id}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
      {log.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {visible.map((n) => (
            <li key={n.id} className={`text-sm flex gap-2 ${n.system ? 'text-gray-500 italic' : 'text-gray-800'}`} data-testid={`note-${n.id}`}>
              {n.system && <Bot className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />}
              <div className="min-w-0">
                <span className="whitespace-pre-wrap break-words">{n.text}</span>
                <span className="ml-2 text-[11px] text-gray-500 whitespace-nowrap">— {n.author}, {n.created_at ? fmtWhen(n.created_at) : ''}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {log.length > 3 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-1.5 text-xs font-semibold text-[#1f4030] hover:underline" data-testid={`notes-toggle-${row.id}`}>
          {showAll ? 'Show fewer' : `Show all ${log.length} notes`}
        </button>
      )}
    </div>
  );
}

const PAY_METHODS = ['Worldpay (Card)', 'Stripe (Card)', 'Cash', 'Bank Transfer'];
const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtCourseDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'Date TBC');

/** Action-area button: "Book onto course" (or "View course" once booked). Opens the shared booking dialog. */
export function BookOntoCourseButton({ row, onOpen }) {
  const navigate = useNavigate();
  if (row.status === 'booked') {
    return row.booked_course_id ? (
      <Button variant="outline" size="sm" onClick={() => navigate(`/staff/training-courses/${row.booked_course_id}`)} className="h-8 text-emerald-800 border-emerald-300 hover:bg-emerald-50" data-testid={`view-booking-${row.id}`}>
        <GraduationCap className="w-3.5 h-3.5 mr-1" /> View course
      </Button>
    ) : null;
  }
  return (
    <Button size="sm" onClick={onOpen} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`book-course-${row.id}`}>
      <GraduationCap className="w-3.5 h-3.5 mr-1" /> Book onto course
    </Button>
  );
}

/** Booking form: tick courses, total, deposit + method → register entries + status Booked. Controlled via open/onOpenChange. */
export function BookOntoCourseDialog({ row, open, onOpenChange, onChanged }) {
  const setOpen = onOpenChange;
  const [courses, setCourses] = useState(null);
  const [picked, setPicked] = useState([]);
  const [total, setTotal] = useState('');
  const [deposit, setDeposit] = useState('');
  const [method, setMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [guests, setGuests] = useState([]); // plus-ones: [{ name, phone }]
  const setGuest = (i, patch) => setGuests((g) => g.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const validGuests = guests.filter((g) => g.name.trim());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || courses) return;
    fetch(`${API_URL}/api/staff/training-courses`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const today = todayIso();
        setCourses((Array.isArray(list) ? list : []).filter((c) => !c.date || c.date >= today).sort((a, b) => (a.date || '').localeCompare(b.date || '')));
      })
      .catch(() => setCourses([]));
  }, [open, courses]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const totalNum = Number(total) || 0;
  const depositNum = Number(deposit) || 0;
  const balance = Math.max(0, totalNum - depositNum);
  const canSubmit = picked.length > 0 && totalNum > 0 && depositNum <= totalNum && (depositNum === 0 || method) && !busy && guests.every((g) => g.name.trim());

  const submit = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-enquiries/${row.id}/book`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_ids: picked, total_amount: totalNum, deposit_amount: depositNum, deposit_method: depositNum > 0 ? method : null, notes: notes.trim() || null,
          guests: validGuests.map((g) => ({ name: g.name.trim(), phone: g.phone.trim() || null })) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'Booking failed');
      onChanged(body.enquiry);
      toast.success(`${row.first_name}${validGuests.length ? ` +${validGuests.length}` : ''} booked onto ${body.created.length} course${body.created.length === 1 ? '' : 's'} · balance ${gbp(body.balance_owed)}${body.skipped.length ? ` · ${body.skipped.length} already on register` : ''}`);
      setOpen(false);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid={`book-course-dialog-${row.id}`}>
          <DialogHeader>
            <DialogTitle>Book {row.first_name} {row.last_name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 -mt-2">{row.email} · {row.phone || 'no phone'} — welcome emails go out automatically.</p>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Courses booked <span className="font-normal normal-case">(tick all that apply)</span></p>
            {courses === null ? <p className="text-sm text-gray-500">Loading courses…</p>
              : courses.length === 0 ? <p className="text-sm text-rose-600">No upcoming courses — create one under Training Courses first.</p>
              : (
                <div className="max-h-72 overflow-y-auto border rounded-lg divide-y" data-testid={`book-course-list-${row.id}`}>
                  {courses.map((c) => {
                    const pal = accentForCourse(c);
                    const on = picked.includes(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer transition-colors hover:brightness-[0.98]"
                        style={{ borderLeft: `5px solid ${pal.primary}`, backgroundColor: on ? pal.tint : '#fff' }} data-testid={`book-course-row-${row.id}-${c.id}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle(c.id)} className="h-4 w-4" style={{ accentColor: pal.primary }} data-testid={`book-course-tick-${row.id}-${c.id}`} />
                        <span className="flex-1 min-w-0">
                          <span className="block text-lg sm:text-xl font-bold leading-tight" style={{ color: pal.primary }} data-testid={`book-course-date-${row.id}-${c.id}`}>{fmtCourseDate(c.date)}</span>
                          <span className="block text-sm font-semibold text-gray-800 truncate">{c.name}</span>
                          {c.registrations_total != null && <span className="block text-xs text-gray-500">{c.registrations_total} booked</span>}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold shrink-0" style={{ backgroundColor: pal.primary, color: pal.ink }}>{brandLabelForCourse(c)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
          </div>

          <div data-testid={`book-guests-${row.id}`}>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Plus ones <span className="font-normal normal-case">(coming with {row.first_name} — paid within the total)</span></p>
              <button type="button" onClick={() => setGuests((g) => [...g, { name: '', phone: '' }])} disabled={guests.length >= 10}
                className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900" data-testid={`book-guest-add-${row.id}`}>
                <UserPlus className="w-3.5 h-3.5" /> Add a +1
              </button>
            </div>
            {guests.length === 0 && <p className="text-xs text-gray-400">Nobody else — just {row.first_name}.</p>}
            {guests.map((g, i) => (
              <div key={i} className="flex items-center gap-2 mb-1.5" data-testid={`book-guest-row-${row.id}-${i}`}>
                <span className="text-[10px] font-bold text-gray-400 w-6 shrink-0">+{i + 1}</span>
                <Input value={g.name} onChange={(e) => setGuest(i, { name: e.target.value })} placeholder="Full name *" maxLength={120} className="h-9" data-testid={`book-guest-name-${row.id}-${i}`} />
                <Input value={g.phone} onChange={(e) => setGuest(i, { phone: e.target.value })} placeholder="Mobile (optional)" maxLength={40} className="h-9 w-40" data-testid={`book-guest-phone-${row.id}-${i}`} />
                <button type="button" onClick={() => setGuests((arr) => arr.filter((_, j) => j !== i))} className="p-1.5 text-gray-400 hover:text-rose-600" aria-label="Remove" data-testid={`book-guest-remove-${row.id}-${i}`}><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Total for {validGuests.length ? `${validGuests.length + 1} people` : 'course' + (picked.length > 1 ? 's' : '')} (£)</span>
              <Input type="number" min="0" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="e.g. 534.00" data-testid={`book-total-${row.id}`} />
            </label>
            <label className="text-sm">
              <span className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Deposit paid (£)</span>
              <Input type="number" min="0" step="0.01" value={deposit} onChange={(e) => setDeposit(e.target.value)} placeholder="0.00" data-testid={`book-deposit-${row.id}`} />
            </label>
          </div>
          {depositNum > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Deposit paid by</p>
              <div className="flex flex-wrap gap-1.5" data-testid={`book-method-${row.id}`}>
                {PAY_METHODS.map((m) => (
                  <button key={m} type="button" onClick={() => setMethod(m)} className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${method === m ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-300'}`} data-testid={`book-method-${row.id}-${m.replace(/[^a-z]/gi, '').toLowerCase()}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-lg bg-gray-50 border px-3 py-2 text-sm space-y-0.5" data-testid={`book-summary-${row.id}`}>
            <div className="flex justify-between"><span className="text-gray-600">Total</span><span className="font-semibold">{gbp(totalNum)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Deposit{method && depositNum > 0 ? ` · ${method}` : ''}</span><span className="font-semibold">−{gbp(depositNum)}</span></div>
            <div className="flex justify-between border-t pt-1 mt-1"><span className="font-bold text-gray-900">Balance owed</span><span className={`font-bold ${balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid={`book-balance-${row.id}`}>{gbp(balance)}</span></div>
            {depositNum > totalNum && <p className="text-xs text-rose-600">Deposit can't be more than the total.</p>}
          </div>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Booking notes (optional) — e.g. partner + son, bringing own PPE" maxLength={2000} data-testid={`book-notes-${row.id}`} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={!canSubmit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`book-course-go-${row.id}`}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <GraduationCap className="w-4 h-4 mr-1" />} Confirm booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}

const SMS_PRESETS = (row, agent) => {
  const first = row.first_name || 'there';
  const who = agent ? `${agent} from Ox Resin Training` : 'Ox Resin Training';
  return [
    { key: 'missed', label: 'Tried to call', text: `Hi ${first}, it's ${who}. I've just tried to call about your training enquiry but couldn't get through. When's a good time to ring you back? Or reply here and I'll sort it by text.` },
    { key: 'dates', label: 'Send dates', text: `Hi ${first}, it's ${who}. Thanks for your enquiry — I'll email our upcoming course dates over now. Any questions just reply here or give me a call.` },
    { key: 'deposit', label: 'Deposit chase', text: `Hi ${first}, it's ${who}. Just checking in on the course place we discussed — shall I send the bank details over so we can secure your spot?` },
  ];
};

/** "Tried calling, no answer" → text the lead. Logged in the notes trail. */
export function SmsLeadButton({ row, agentName, onChanged }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const presets = SMS_PRESETS(row, agentName);
  useEffect(() => { if (open) setText(presets[0].text); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!row.phone) return null;

  const send = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-enquiries/${row.id}/sms`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(body) || 'SMS failed');
      onChanged(body.enquiry);
      toast.success(`Text sent to ${row.first_name} on ${row.phone}`);
      setOpen(false);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="h-8 border-sky-300 text-sky-800 hover:bg-sky-50" data-testid={`sms-lead-${row.id}`}>
        <MessageSquare className="w-3.5 h-3.5 mr-1" /> SMS
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-lg" data-testid={`sms-lead-dialog-${row.id}`}>
          <DialogHeader>
            <DialogTitle>Text {row.first_name} · {row.phone}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <button key={p.key} type="button" onClick={() => setText(p.text)} className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${text === p.text ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-700 border-gray-200 hover:border-sky-300'}`} data-testid={`sms-preset-${p.key}-${row.id}`}>
                {p.label}
              </button>
            ))}
          </div>
          <textarea
            value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={480}
            className="w-full text-sm border-2 border-gray-200 rounded-lg p-3 focus:outline-none focus:border-sky-500"
            data-testid={`sms-text-${row.id}`}
          />
          <div className="flex items-center justify-between text-[11px] text-gray-500">
            <span>{text.length}/480 · {Math.max(1, Math.ceil(text.length / 160))} SMS part{text.length > 160 ? 's' : ''}</span>
            <span>Sent via FireText · logged in the notes</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={send} disabled={busy || !text.trim()} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid={`sms-send-${row.id}`}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send text
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
